// ----------------------------------------------------------------------------
// fx-rack — Per-track FX rack for DAWSEUR
//
// Uses the shared afx.js effect implementations and audiofx.js definitions.
// Each track has its own FX chain in the playback engine.
// The FX rack panel shows the selected track's effects.
// ----------------------------------------------------------------------------

const afx = require('../../common/afx.js');
const fxs = require('../../common/audiofx.js');
const { faderH } = require('../../common/ctrlz.js');
const Sortable = require('sortablejs');

function createFxRack(opts = {}) {
   const container = document.getElementById('lw-fx-rack-body');
   const titleEl = document.getElementById('lw-fx-rack-title');
   const addBtn = document.getElementById('lw-fx-add');
   if (!container) return null;

   let sortable = null;

   const state = {
      currentTrackId: null,
      engine: opts.engine || null,
      onFxAdded: opts.onFxAdded || (() => {}),
      onFxRemoved: opts.onFxRemoved || (() => {}),
      onBeforeFxChange: opts.onBeforeFxChange || (() => {}),
   };

   // ---- FX picker modal ----
   const fxPickerModal = document.getElementById('lw-fx-picker-modal');
   const fxPickerClose = document.getElementById('lw-fx-picker-close');
   const fxPickerSearch = document.getElementById('lw-fx-picker-search');
   const fxPickerList = document.getElementById('lw-fx-picker-list');

   function buildFxPickerList(filter) {
      if (!fxPickerList) return;
      fxPickerList.innerHTML = '';
      const lower = (filter || '').toLowerCase();
      for (const fx of fxs.fx) {
         const name = fx.display || fx.name;
         if (lower && !name.toLowerCase().includes(lower)) continue;
         const item = document.createElement('li');
         item.className = 'bkm-item';
         const row = document.createElement('div');
         row.className = 'bkm-item-row';
         const label = document.createElement('span');
         label.className = 'bkm-item-name';
         label.textContent = name;
         row.appendChild(label);
         item.appendChild(row);
         item.addEventListener('click', () => {
            addEffect(fx);
            closeFxPicker();
         });
         fxPickerList.appendChild(item);
      }
   }

   function openFxPicker() {
      if (state.currentTrackId === null) return;
      if (!fxPickerModal) return;
      buildFxPickerList('');
      if (fxPickerSearch) fxPickerSearch.value = '';
      fxPickerModal.hidden = false;
      if (fxPickerSearch) setTimeout(() => fxPickerSearch.focus(), 50);
   }

   function closeFxPicker() {
      if (fxPickerModal) fxPickerModal.hidden = true;
   }

   if (addBtn) {
      addBtn.addEventListener('click', (e) => {
         e.stopPropagation();
         openFxPicker();
      });
   }

   if (fxPickerClose) {
      fxPickerClose.addEventListener('click', closeFxPicker);
   }

   if (fxPickerModal) {
      fxPickerModal.addEventListener('click', (e) => {
         if (e.target === fxPickerModal) closeFxPicker();
      });
   }

   if (fxPickerSearch) {
      fxPickerSearch.addEventListener('input', () => {
         buildFxPickerList(fxPickerSearch.value);
      });
      fxPickerSearch.addEventListener('keydown', (e) => {
         if (e.key === 'Escape') closeFxPicker();
      });
   }

   document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && fxPickerModal && !fxPickerModal.hidden) {
         closeFxPicker();
      }
   });

   // ---- Add an effect to the current track ----
   function addEffect(fxDef) {
      if (state.currentTrackId === null || !state.engine) return;
      const track = state.engine.getTrack(state.currentTrackId);
      if (!track) return;

      // Push history before the change so it's undoable
      state.onBeforeFxChange();

      // Build UI first, collect DOM elements for the audio effect
      const { holdbox, fxbox, eleminputs } = buildFxUI(fxDef);

      // Create the audio effect with DOM element references
      const fxObject = fxDef.fn(track.gainNode.context, eleminputs);
      fxObject._def = fxDef;
      fxObject._controls = eleminputs;

      // Wire delete button
      const delBtn = holdbox.querySelector('.xbtn.del');
      if (delBtn) {
         delBtn.addEventListener('click', () => {
            removeEffect(fxObject);
            holdbox.remove();
         });
      }

      // Wire on/off switch — rebuild FX chain to bypass/enable this effect
      const switchInput = holdbox.querySelector('.switch');
      if (switchInput) {
         switchInput.addEventListener('change', () => {
            fxbox.dataset.ison = switchInput.checked ? 'true' : 'false';
            fxObject._bypassed = !switchInput.checked;
            if (state.engine) state.engine.setTrackFx(state.currentTrackId, true);
         });
      }

      // Wire parameter change events for automation
      for (const input of fxDef.inputs) {
         const el = eleminputs[input.name];
         if (!el) continue;
         el.addEventListener('change', () => {
            const val = el.type === 'select-one' ? el.value : parseFloat(el.value);
            holdbox.dispatchEvent(new CustomEvent('fxvaluechange', {
               bubbles: true,
               detail: { fxId: fxObject.id, param: input.name, value: val }
            }));
         });
         if (el.type === 'range' || el.type === 'number') {
            el.addEventListener('contextmenu', (e) => {
               e.preventDefault();
               showParamContextMenu(e.clientX, e.clientY, fxObject, input.name, el);
            });
         }
      }

      // Add to engine
      state.engine.addTrackFx(state.currentTrackId, fxObject);

      state.onFxAdded(state.currentTrackId, fxObject);
   }

   // ---- Build UI for a single effect (returns elements + eleminputs) ----
   function buildFxUI(fxDef) {
      const holdbox = document.createElement('div');
      holdbox.className = 'fxhold';

      const fxbox = document.createElement('div');
      fxbox.className = 'fxbox ' + fxDef.name;
      fxbox.dataset.ison = 'true';

      // Header
      const handle = document.createElement('div');
      handle.className = 'handle';
      handle.textContent = fxDef.display || fxDef.name;

      const topfx = document.createElement('div');
      topfx.className = 'topfx';

      const delBtn = document.createElement('button');
      delBtn.className = 'xbtn del';
      delBtn.textContent = '✕';

      const switchId = 'fx_sw_' + Math.random().toString(36).substr(2, 9);
      const switchInput = document.createElement('input');
      switchInput.type = 'checkbox';
      switchInput.checked = true;
      switchInput.className = 'switch';
      switchInput.id = switchId;
      const switchLabel = document.createElement('label');
      switchLabel.className = 'switchlabel';
      switchLabel.htmlFor = switchId;

      topfx.appendChild(delBtn);
      topfx.appendChild(switchInput);
      topfx.appendChild(switchLabel);

      // Controls grid
      const ctrlgrid = document.createElement('div');
      ctrlgrid.className = 'ctrlgrid';

      const eleminputs = {};

      for (const input of fxDef.inputs) {
         if (input.type === 'knob') {
            const holder = document.createElement('div');
            holder.className = 'knobholder';
            const rangeInput = document.createElement('input');
            rangeInput.type = 'number';
            rangeInput.classList.add('ctrlinput');
            rangeInput.dataset.label = input.name;
            rangeInput.dataset.nocurve = '1';
            rangeInput.dataset.trackId = state.currentTrackId;
            rangeInput.min = input.min;
            rangeInput.max = input.max;
            rangeInput.step = input.step;
            rangeInput.value = input.value;
            holder.appendChild(rangeInput);
            ctrlgrid.appendChild(holder);

            try { faderH(rangeInput); } catch (e) { console.warn('[fx-rack] faderH failed:', e); }
            eleminputs[input.name] = rangeInput;
         } else if (input.type === 'select') {
            const sel = document.createElement('select');
            sel.className = 'lw-select fx-select';
            for (const opt of input.options) {
               const o = document.createElement('option');
               o.value = opt.value;
               o.textContent = opt.name.toString().replace(/([A-Z])/g, ' $1');
               sel.appendChild(o);
            }
            ctrlgrid.appendChild(sel);
            eleminputs[input.name] = sel;
         }
      }

      fxbox.appendChild(handle);
      const fxcontent = document.createElement('div');
      fxcontent.className = 'fxcontent';
      fxcontent.appendChild(topfx);
      fxcontent.appendChild(ctrlgrid);
      fxbox.appendChild(fxcontent);
      holdbox.appendChild(fxbox);
      container.appendChild(holdbox);

      return { holdbox, fxbox, eleminputs };
   }

   // ---- Remove an effect ----
   function removeEffect(fxObject) {
      if (state.currentTrackId === null || !state.engine) return;
      // Push history before the change so it's undoable
      state.onBeforeFxChange();
      // Find index in engine's fx chain
      const chain = state.engine.getTrackFx(state.currentTrackId);
      const idx = chain.indexOf(fxObject);
      if (idx >= 0) {
         state.engine.removeTrackFx(state.currentTrackId, idx);
      }
      state.onFxRemoved(state.currentTrackId, fxObject);
   }

   // ---- Context menu for parameters (Create Automation) ----
   function showParamContextMenu(x, y, fxObject, paramName, inputEl) {
      let menu = document.getElementById('lw-fx-param-menu');
      if (menu) menu.remove();
      menu = document.createElement('div');
      menu.id = 'lw-fx-param-menu';
      menu.className = 'lw-context-menu';
      menu.style.left = x + 'px';
      menu.style.top = y + 'px';

      const item = document.createElement('div');
      item.className = 'lw-ctx-item';
      item.textContent = 'Create Automation';
      item.addEventListener('click', () => {
         menu.remove();
         if (opts.onCreateAutomation) {
            opts.onCreateAutomation(state.currentTrackId, fxObject, paramName, inputEl);
         }
      });
      menu.appendChild(item);

      const closeItem = document.createElement('div');
      closeItem.className = 'lw-ctx-item';
      closeItem.textContent = 'Cancel';
      closeItem.addEventListener('click', () => menu.remove());
      menu.appendChild(closeItem);

      document.body.appendChild(menu);
      setTimeout(() => {
         document.addEventListener('click', function close() {
            menu.remove();
            document.removeEventListener('click', close);
         }, { once: true });
      }, 0);
   }

   // ---- Show FX rack for a track ----
   function showTrack(trackId) {
      state.currentTrackId = trackId;
      // Destroy old sortable instance before clearing DOM
      if (sortable) {
         sortable.destroy();
         sortable = null;
      }
      container.innerHTML = '';
      if (titleEl) {
         titleEl.textContent = trackId !== null ? `FX Rack — Track ${trackId}` : 'FX Rack — select a track';
      }
      if (trackId === null || !state.engine) return;

      // Rebuild UI for existing effects — just rebuild the DOM, audio objects persist
      const chain = state.engine.getTrackFx(trackId);
      for (const fx of chain) {
         if (!fx._def) continue;
         const { holdbox, fxbox, eleminputs } = buildFxUI(fx._def);
         // Re-link DOM elements to the existing audio object
         fx._controls = eleminputs;
         // Wire delete
         const delBtn = holdbox.querySelector('.xbtn.del');
         if (delBtn) delBtn.addEventListener('click', () => { removeEffect(fx); holdbox.remove(); });
         // Wire on/off
         const sw = holdbox.querySelector('.switch');
         if (sw) sw.addEventListener('change', () => {
            fxbox.dataset.ison = sw.checked ? 'true' : 'false';
            fx._bypassed = !sw.checked;
            if (state.engine) state.engine.setTrackFx(state.currentTrackId, true);
         });
         // Wire param changes
         for (const input of fx._def.inputs) {
            const el = eleminputs[input.name];
            if (!el) continue;
            el.addEventListener('change', () => {
               const val = el.type === 'select-one' ? el.value : parseFloat(el.value);
               holdbox.dispatchEvent(new CustomEvent('fxvaluechange', {
                  bubbles: true, detail: { fxId: fx.id, param: input.name, value: val }
               }));
            });
            if (el.type === 'number' || el.type === 'range') {
               el.addEventListener('contextmenu', (e) => {
                  e.preventDefault();
                  showParamContextMenu(e.clientX, e.clientY, fx, input.name, el);
               });
            }
         }
      }

      // Create Sortable instance for drag & drop reordering via the handle label
      sortable = Sortable.create(container, {
         handle: '.handle',
         animation: 150,
         axis: 'y',
         onEnd: (evt) => {
            if (evt.oldIndex === evt.newIndex) return;
            // Push history before the reorder so it's undoable
            state.onBeforeFxChange();
            if (state.engine && state.engine.moveTrackFx) {
               state.engine.moveTrackFx(state.currentTrackId, evt.oldIndex, evt.newIndex);
            }
         }
      });
   }

   // ---- Set current track (for programmatic FX loading) ----
   function setCurrentTrack(trackId) {
      state.currentTrackId = trackId;
   }

   // ---- Add effect by type name (for song loading) ----
   function addEffectByName(typeName, params, bypassed) {
      const fxDef = fxs.fx.find(f => f.name === typeName);
      if (!fxDef) {
         console.warn('[fx-rack] Unknown FX type: ' + typeName);
         return;
      }
      addEffect(fxDef);
      // Apply params and bypass state
      if (state.currentTrackId !== null && state.engine) {
         const chain = state.engine.getTrackFx(state.currentTrackId);
         const fxObj = chain[chain.length - 1];
         if (fxObj && fxObj._controls) {
            for (const [paramName, value] of Object.entries(params || {})) {
               const el = fxObj._controls[paramName];
               if (el) {
                  el.value = value;
                  el.dispatchEvent(new Event('change', { bubbles: true }));
               }
            }
         }
         if (fxObj && bypassed) {
            fxObj._bypassed = true;
            state.engine.setTrackFx(state.currentTrackId, true);
            // Update UI switch
            const holdbox = fxObj._controls ? null : null; // Can't easily access holdbox from here
         }
      }
   }

   return {
      state,
      addEffect,
      addEffectByName,
      setCurrentTrack,
      removeEffect,
      showTrack,
   };
}

module.exports = { createFxRack };
